import Typography from '@mui/material/Typography'
import { append, path, update } from 'ramda'
import React from 'react'

import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import {
  CampaignFileResult,
  CampaignTemplateType,
  ConditionActions,
  FilesState,
  ImageProps,
  Language,
  SenderIdOption,
  VoiceProps,
} from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { CountryTimezone, FileUploadType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import CallResultsActions, { CallResultKey } from './call-results-actions'

type Props = {
  actions: ConditionActions[]
  campaignType: CampaignTemplateType
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: FilesState
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  timezones: TimezoneWithCountryCodeType
  timezone: string
  token: string
  voiceList: BaseLanguageType[]
  voicePlayingId?: string
  whatsappSenderIds: SenderIdOption[]

  changeHandler: (p: { value: ConditionActions[] }) => void
  getFile: (id: string, file: string) => Promise<CampaignFileResult>
  onMicAccessError: () => void
  saveFile: (id: string, file: FileUploadType) => Promise<CampaignFileResult>
  setPlayingId: (playId?: string) => void
}

const id = 'call-results-actions'

export function CallResults({
  actions,
  files,
  voiceList,
  voicePlayingId,
  changeHandler,
  getFile,
  onMicAccessError,
  saveFile,
  setPlayingId,
  ...props
}: Props) {
  const onChildChange = React.useCallback(
    ({ result, value }: { result: CallResultKey; value: Action[] }) => {
      changeHandler({
        value: updateCallResultActions({
          result,
          value,
          actions,
        }),
      })
    },
    [actions, changeHandler],
  )

  const answeredActions = React.useMemo(() => {
    const found = actions.find((a) => path(['when', 2], a) === 'answered')
    return found?.actions || []
  }, [actions])
  const notAnsweredActions = React.useMemo(() => {
    const found = actions.find((a) => path(['when', 2], a) === 'notAnswered')
    return found?.actions || []
  }, [actions])
  const errorActions = React.useMemo(() => {
    const found = actions.find((a) => path(['when', 2], a) === 'error')
    return found?.actions || []
  }, [actions])

  const handleGetFile = React.useCallback((file: string) => getFile(id, file), [getFile])
  const handleSaveFile = React.useCallback((file: FileUploadType) => saveFile(id, file), [saveFile])
  const getImageProps = React.useCallback(
    (qid: string): ImageProps => ({
      id: qid,
      loading: files.loadingItems.indexOf(qid) > -1,
      getFile: handleGetFile,
      saveFileHandler: handleSaveFile,
    }),
    [files.loadingItems, handleGetFile, handleSaveFile],
  )
  const getVoiceProps = React.useCallback(
    (qid: string): VoiceProps => ({
      id: qid,
      files: files.data || {},
      filterType: props.campaignType,
      loading: files.loadingItems.indexOf(qid) > -1,
      personalizationList: props.personalizationList,
      playId: voicePlayingId,
      voiceList,
      getFile: handleGetFile,
      saveFileHandler: handleSaveFile,
      setPlaying: setPlayingId,
      onMicAccessError,
    }),
    [
      props.campaignType,
      files.data,
      files.loadingItems,
      props.personalizationList,
      voiceList,
      voicePlayingId,
      handleGetFile,
      handleSaveFile,
      onMicAccessError,
      setPlayingId,
    ],
  )

  const commonProps = {
    ...props,
    files: files.data,
    getImageProps,
    getVoiceProps,
  }

  return (
    <div>
      <Typography variant="subtitle1" color="textSecondary">
        Specify what we should do if the call is (not) answered or cannot be made. We will take these actions only after
        all call retries are done.
      </Typography>
      <CallResultsActions
        {...commonProps}
        actions={answeredActions}
        resultKey="answered"
        changeHandler={onChildChange}
      />
      <CallResultsActions
        {...commonProps}
        actions={notAnsweredActions}
        resultKey="notAnswered"
        changeHandler={onChildChange}
      />
      <CallResultsActions {...commonProps} actions={errorActions} resultKey="error" changeHandler={onChildChange} />
    </div>
  )
}

export const updateCallResultActions = ({
  result,
  value,
  actions,
}: {
  result: CallResultKey
  value: Action[]
  actions: ConditionActions[]
}) => {
  const actionTemplate: ConditionActions = {
    when: ['==', '{{call.result}}', result],
    actions: value,
  }
  const index = actions.findIndex((a) => a.when?.[2] === result)

  if (index !== -1) {
    return update(index, actionTemplate, actions)
  }
  return append(actionTemplate, actions)
}

export default CallResults
